import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import {
  CAST_SCALE_OPTIONS,
  POPUP_AFTER_MESSAGES_OPTIONS,
  POPUP_AFTER_MINUTES_OPTIONS,
  QUESTION_VARIANT_OPTIONS,
  SurveyQuestionVariant,
  SurveySettingsDto,
  UpdateSurveySettingsDto,
} from '@/Domain/api/WIdget/dto/SurveySettings.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';

// Radix Select can't bind to null; use a sentinel for the "off" option.
const OFF_SENTINEL = '__off__';

const schema = z.object({
  popup_after_messages: z.string(),
  popup_after_minutes: z.string(),
  cast_scale: z.enum(['2_step', '3_step', '5_step']),
  question_variant: z.enum([
    'need_more_help',
    'anything_else_help',
    'anything_else_do',
    'need_human_assistance',
  ]),
});

type FormValues = z.infer<typeof schema>;

const questionVariantItems = (current: SurveyQuestionVariant): SurveyQuestionVariant[] =>
  QUESTION_VARIANT_OPTIONS.includes(current)
    ? QUESTION_VARIANT_OPTIONS
    : [...QUESTION_VARIANT_OPTIONS, current];

const toFormValues = (settings: SurveySettingsDto): FormValues => ({
  popup_after_messages:
    settings.popup_after_messages === null ? OFF_SENTINEL : String(settings.popup_after_messages),
  popup_after_minutes:
    settings.popup_after_minutes === null ? OFF_SENTINEL : String(settings.popup_after_minutes),
  cast_scale: settings.cast_scale,
  question_variant: settings.question_variant,
});

const toPayload = (values: FormValues): UpdateSurveySettingsDto => ({
  popup_after_messages:
    values.popup_after_messages === OFF_SENTINEL ? null : Number(values.popup_after_messages),
  popup_after_minutes:
    values.popup_after_minutes === OFF_SENTINEL ? null : Number(values.popup_after_minutes),
  cast_scale: values.cast_scale,
  question_variant: values.question_variant,
});

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const SurveySettingsSheet = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation('website', { keyPrefix: 'conversation_rating.survey_settings' });

  const queryClient = useQueryClient();

  // Always stale, so reopening the sheet revalidates rather than seeding the form from a
  // cached object that another tab (or an earlier save) has since superseded.
  const {
    data: settings,
    isError,
    isFetching,
  } = useQuery<SurveySettingsDto>({
    queryKey: [QueryKey.conversationRatingConfiguration.surveySettings],
    enabled: open,
    staleTime: 0,
  });
  // Cached data is not enough: reopening starts a revalidation, and saving the stale full
  // form would overwrite whatever another tab has since written. A failed revalidation
  // leaves the cached object in place with isFetching back to false, so isError has to
  // keep the form shut too rather than letting Save light up beside the error.
  const isLoaded = settings !== undefined && !isFetching && !isError;

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      popup_after_messages: '2',
      popup_after_minutes: '5',
      cast_scale: '5_step',
      question_variant: 'need_more_help',
    },
  });

  // Keyed on `open` as well as the data: the sheet stays mounted after Cancel, and the
  // cached settings object is identical on reopen, so without this the abandoned edits
  // survive and the next Save persists them.
  useEffect(() => {
    if (open && settings) {
      reset(toFormValues(settings));
    }
  }, [open, settings, reset]);

  const mutation = useMutation<SurveySettingsDto, unknown, UpdateSurveySettingsDto>({
    mutationKey: [QueryKey.conversationRatingConfiguration.surveySettingsPatch],
    // Overriding onSuccess replaces the mutation default that stores the response, so the
    // cache has to be written here too or the next session starts from pre-save values.
    onSuccess: (updated) => {
      queryClient.setQueryData([QueryKey.conversationRatingConfiguration.surveySettings], updated);
      toast.success(t('saved'));
      onOpenChange(false);
    },
  });

  const onSubmit = (values: FormValues) => mutation.mutate(toPayload(values));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex flex-col gap-6 overflow-y-auto"
        data-testid="survey-settings-sheet"
      >
        <SheetHeader>
          <SheetTitle>{t('title')}</SheetTitle>
          <p className="text-sm text-muted-foreground">{t('description')}</p>
        </SheetHeader>

        {isError && (
          <p className="text-sm text-destructive" data-testid="survey-settings-error">
            {t('load_error')}
          </p>
        )}

        <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
          {/* Saving before the query lands would persist the form's placeholder defaults. */}
          <fieldset
            className="flex flex-col gap-6 border-0 p-0 disabled:opacity-50"
            disabled={!isLoaded}
          >
            <section className="flex flex-col gap-4">
              <h4 className="text-sm font-semibold">{t('popup_section')}</h4>

              <div className="flex flex-col gap-2">
                <Label htmlFor="popup_after_messages">{t('after_messages')}</Label>
                <Controller
                  control={control}
                  name="popup_after_messages"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="popup_after_messages" data-testid="survey-after-messages">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={OFF_SENTINEL}>{t('off')}</SelectItem>
                        {POPUP_AFTER_MESSAGES_OPTIONS.map((option) => (
                          <SelectItem key={option} value={String(option)}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="popup_after_minutes">{t('after_minutes')}</Label>
                <Controller
                  control={control}
                  name="popup_after_minutes"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="popup_after_minutes" data-testid="survey-after-minutes">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={OFF_SENTINEL}>{t('off')}</SelectItem>
                        {POPUP_AFTER_MINUTES_OPTIONS.map((option) => (
                          <SelectItem key={option} value={String(option)}>
                            {t('minutes', { count: option })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="question_variant">{t('question_style')}</Label>
                <Controller
                  control={control}
                  name="question_variant"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="question_variant" data-testid="survey-question-variant">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {questionVariantItems(field.value).map((option) => (
                          <SelectItem
                            key={option}
                            value={option}
                            // A withheld variant a newer revision stored: shown so the
                            // trigger has a label, disabled so it cannot be chosen here.
                            disabled={!QUESTION_VARIANT_OPTIONS.includes(option)}
                          >
                            {t(`question_variant_options.${option}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </section>

            <section className="flex flex-col gap-4">
              <h4 className="text-sm font-semibold">{t('final_rating_section')}</h4>

              <div className="flex flex-col gap-2">
                <Label htmlFor="cast_scale">{t('rating_scale')}</Label>
                <Controller
                  control={control}
                  name="cast_scale"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="cast_scale" data-testid="survey-cast-scale">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CAST_SCALE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {t(`cast_scale_options.${option}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <p className="text-sm text-muted-foreground">{t('rating_scale_hint')}</p>
              </div>
            </section>
          </fieldset>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              data-testid="survey-settings-save"
              disabled={!isLoaded || mutation.isPending}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};
